import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from '../../ui/DatePicker';
import { toast } from '../../ui/Toast';
import { isAlreadyGone } from '../../../api/client';
import { Button } from '../../ui/Button';
import { UserAvatar } from '../../ui/UserAvatar';
import { Icon, MapPin, Plane, Plus, Sparkles, Trash2 } from '../../ui/Icon';
import { flightsApi, type ExtractedFlightGroup } from '../../../api/flights.api';
import { localInputToISO, isoToLocalInput } from '../../../utils/dateTime';
import { useAuthStore } from '../../../stores/auth.store';
import { useDocumentExtraction, uploadStagedFiles } from '../../../hooks/useDocumentExtraction';
import type { Flight, FlightDocument, FlightStop, TripParticipant } from '../../../types';
import { DocumentAttachments } from './DocumentAttachments';
import { ReminderField } from './ReminderField';
import { ExtractingPlaceholder } from './ExtractingPlaceholder';
import { FIELD_LABEL } from './documentFiles';

interface Props {
  tripId: string;
  tripStartDate?: string;
  initial?: Flight;
  initialFiles?: File[];
  participants: TripParticipant[];
  onSaved: (f: Flight) => void;
  /** A round trip yields two flights — the first is reported through this. */
  onExtraFlightCreated?: (f: Flight) => void;
  /** Extracted flights after the one on screen, still unsaved: closing now drops them. */
  onUnreviewedChange?: (count: number) => void;
  onCancel: () => void;
}

interface StopFormState {
  fromAirport: string;
  toAirport: string;
  departureTime: string;   // 'YYYY-MM-DDTHH:mm' for DatePicker
  arrivalTime: string;
  flightNumber: string;
  airline: string;
  reminderMinutes: number | null;
}

const EMPTY_STOP: StopFormState = {
  fromAirport: '', toAirport: '', departureTime: '', arrivalTime: '',
  flightNumber: '', airline: '', reminderMinutes: null,
};

function initialStops(initial?: Flight): StopFormState[] {
  if (initial?.stops?.length) {
    return initial.stops.map((s: FlightStop) => ({
      fromAirport:     s.fromAirport,
      toAirport:       s.toAirport,
      departureTime:   s.departureTime ? isoToLocalInput(s.departureTime) : '',
      arrivalTime:     s.arrivalTime   ? isoToLocalInput(s.arrivalTime)   : '',
      flightNumber:    s.flightNumber  ?? '',
      airline:         s.airline       ?? '',
      reminderMinutes: s.reminderMinutes ?? null,
    }));
  }
  return [{
    ...EMPTY_STOP,
    fromAirport:   initial?.departureAirport ?? '',
    toAirport:     initial?.arrivalAirport   ?? '',
    departureTime: initial?.departureTime ? initial.departureTime.slice(0, 16) : '',
    arrivalTime:   initial?.arrivalTime   ? initial.arrivalTime.slice(0, 16)   : '',
    flightNumber:  initial?.flightNumber  ?? '',
    airline:       initial?.airline       ?? '',
  }];
}

/** Accent- and case-insensitive form, for matching extracted passenger names. */
function normalize(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

export function FlightForm({
  tripId, tripStartDate, initial, initialFiles, participants,
  onSaved, onExtraFlightCreated, onUnreviewedChange, onCancel,
}: Props) {
  const { t } = useTranslation();
  const me = useAuthStore(s => s.user);

  // Include the current user if they're not already in the trip participants list
  const allParticipants: TripParticipant[] = me && !participants.some(p => p.userId === me.id)
    ? [{ id: me.id, tripId, userId: me.id, role: 'OWNER', joinedAt: '', user: { id: me.id, username: me.username, displayName: me.displayName, avatarUrl: me.avatarUrl } }, ...participants]
    : participants;

  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const err = (bad: boolean) => bad ? ' input-error' : '';

  const [selectedParticipantIds, setSelectedParticipantIds] = useState<string[]>(
    (initial?.participants ?? []).map(p => p.userId),
  );
  const [extractedGroups, setExtractedGroups] = useState<ExtractedFlightGroup[]>([]);
  const [stepIndex, setStepIndex] = useState(0);
  const unreviewed = Math.max(0, extractedGroups.length - 1 - stepIndex);
  useEffect(() => { onUnreviewedChange?.(unreviewed); }, [unreviewed, onUnreviewedChange]);
  const [existingDocs, setExistingDocs] = useState<FlightDocument[]>(initial?.documents ?? []);
  const [docToDelete, setDocToDelete] = useState<FlightDocument | null>(null);
  const [stops, setStops] = useState<StopFormState[]>(() => initialStops(initial));
  const [v, setV] = useState({
    direction: initial?.direction ?? 'OUTBOUND',
    notes: initial?.notes ?? '',
  });

  function updateStop(i: number, patch: Partial<StopFormState>) {
    setStops(prev => prev.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  }
  function removeStop(i: number) {
    setStops(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);
  }

  function matchParticipants(names: string[]) {
    const matchedIds: string[] = [];
    const unmatched: string[] = [];
    for (const name of names) {
      const norm = normalize(name);
      const match = allParticipants.find(p => {
        const candidate = normalize(p.user.displayName || p.user.username);
        return candidate === norm || candidate.includes(norm) || norm.includes(candidate);
      });
      if (match) matchedIds.push(match.userId);
      else unmatched.push(name);
    }
    return { matchedIds, unmatched };
  }

  function applyExtractedGroupToForm(group: ExtractedFlightGroup) {
    if (group.legs.length > 0) {
      setStops(group.legs.map(l => ({
        fromAirport:     l.fromAirport,
        toAirport:       l.toAirport,
        departureTime:   l.departureTime.slice(0, 16),
        arrivalTime:     l.arrivalTime.slice(0, 16),
        flightNumber:    l.flightNumber ?? '',
        airline:         l.airline      ?? '',
        reminderMinutes: null,
      })));
    }
    setV(x => ({
      direction: group.direction ?? x.direction,
      // Replace rather than merge with any previous state — this can be called
      // again when moving from one extracted step to the next, and the previous
      // step's own notes/PNR shouldn't leak into a different flight's notes.
      notes: group.bookingRef ? `PNR: ${group.bookingRef}` : '',
    }));

    const names = group.passengerNames ?? [];
    if (names.length === 0) return;
    const { matchedIds, unmatched } = matchParticipants(names);
    if (matchedIds.length > 0) {
      setSelectedParticipantIds(prev => Array.from(new Set([...prev, ...matchedIds])));
    }
    if (unmatched.length > 0) {
      toast(t('bookings.toast.extractNoMatch', { names: unmatched.join(', ') }), 'info');
    }
  }

  const files = useDocumentExtraction({
    initialFiles,
    extract: fs => flightsApi.extract(tripId, fs),
    onExtracted: extracted => {
      setExtractedGroups(extracted.flights);
      setStepIndex(0);
      if (extracted.flights[0]) applyExtractedGroupToForm(extracted.flights[0]);
    },
  });

  async function removeExistingDoc(doc: FlightDocument) {
    if (!initial) return;
    try {
      await flightsApi.deleteDocument(tripId, initial.id, doc.id);
    } catch (e) {
      if (!isAlreadyGone(e)) {
        toast(t('bookings.toast.uploadFailed'), 'error');
        setDocToDelete(null);
        return;
      }
    }
    setExistingDocs(prev => prev.filter(d => d.id !== doc.id));
    toast(t('bookings.toast.fileDeleted'), 'success');
    setDocToDelete(null);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    // Every leg must have from/to airports + both times
    const validStops = stops.filter(s => s.fromAirport.trim() && s.toAirport.trim() && s.departureTime && s.arrivalTime);
    if (validStops.length !== stops.length) return;

    setBusy(true);
    try {
      const first = validStops[0];
      const last  = validStops[validStops.length - 1];
      const payload = {
        direction: v.direction,
        notes:     v.notes || undefined,
        participantIds: selectedParticipantIds,
        // Derive main flight fields from first/last leg
        departureAirport: first.fromAirport.toUpperCase(),
        arrivalAirport:   last.toAirport.toUpperCase(),
        departureTime:    localInputToISO(first.departureTime),
        arrivalTime:      localInputToISO(last.arrivalTime),
        flightNumber:     first.flightNumber.trim() || null,
        airline:          first.airline.trim()      || null,
        stops: validStops.map(s => ({
          fromAirport:   s.fromAirport.toUpperCase(),
          toAirport:     s.toAirport.toUpperCase(),
          departureTime: localInputToISO(s.departureTime),
          arrivalTime:   localInputToISO(s.arrivalTime),
          flightNumber:    s.flightNumber.trim() || null,
          airline:         s.airline.trim()      || null,
          reminderMinutes: s.reminderMinutes     ?? null,
        })),
      };
      const result = (initial && stepIndex === 0)
        ? await flightsApi.update(tripId, initial.id, payload)
        : await flightsApi.create(tripId, payload);

      const docs = await uploadStagedFiles(
        files.stagedFiles,
        file => flightsApi.uploadDocument(tripId, result.id, file),
        result.documents ?? [],
        t,
      );
      const resultWithDocs = { ...result, documents: docs };

      // Round-trip tickets describe two journeys (outbound + return) in one
      // document — the form only shows one direction at a time, so once this
      // step is saved, move on to reviewing the next one instead of closing.
      if (stepIndex < extractedGroups.length - 1) {
        onExtraFlightCreated?.(resultWithDocs);
        const nextIndex = stepIndex + 1;
        setStepIndex(nextIndex);
        applyExtractedGroupToForm(extractedGroups[nextIndex]);
        setSubmitted(false);
        toast(t('bookings.toast.flightAdded'), 'success');
      } else {
        onSaved(resultWithDocs);
      }
    } catch {
      toast(t('bookings.toast.saveFailed'), 'error');
    } finally {
      setBusy(false);
    }
  }

  if (files.extracting) return <ExtractingPlaceholder />;

  return (
    <form onSubmit={submit} className="space-y-3">
      {extractedGroups.length > 1 && (
        <div className="flex items-center gap-1.5 text-xs font-semibold text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-xl px-3 py-2">
          <Icon as={Sparkles} size={13} />
          {t('bookings.form.stepIndicator', { current: stepIndex + 1, total: extractedGroups.length })}
        </div>
      )}

      <div>
        <label className={FIELD_LABEL}>{t('bookings.form.direction')}</label>
        <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-800/60">
          {(['OUTBOUND', 'RETURN'] as const).map(d => {
            const active = v.direction === d;
            return (
              <button key={d} type="button"
                onClick={() => setV(x => ({ ...x, direction: d }))}
                className={`relative flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                  active
                    ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                }`}>
                <Icon
                  as={Plane}
                  size={14}
                  className={`transition-colors ${
                    active
                      ? d === 'OUTBOUND' ? 'text-sky-500' : 'text-indigo-500'
                      : 'text-slate-400 dark:text-slate-500'
                  } ${d === 'RETURN' ? 'rotate-180' : ''}`}
                />
                {d === 'OUTBOUND' ? t('bookings.form.outbound') : t('bookings.form.return')}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Legs ── */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className={FIELD_LABEL + ' mb-0'}>{t('bookings.form.legs')}</label>
          <button type="button" onClick={() => setStops(prev => [...prev, { ...EMPTY_STOP }])}
            className="flex items-center gap-1 text-[11px] font-semibold text-teal-600 hover:text-teal-700 cursor-pointer">
            <Icon as={Plus} size={12} /> {t('bookings.form.addLeg')}
          </button>
        </div>

        {/* Route preview */}
        <div className="flex items-center gap-1 mb-2 px-1 overflow-x-auto">
          {stops.map((s, i) => (
            <span key={i} className="flex items-center gap-1 shrink-0">
              {i === 0 && (
                <span className="font-mono text-[11px] font-bold tabular-nums text-slate-700 dark:text-slate-200">
                  {s.fromAirport || '···'}
                </span>
              )}
              <span className="text-slate-300 dark:text-slate-600">→</span>
              <span className={`font-mono text-[11px] tabular-nums ${i < stops.length - 1 ? 'font-semibold text-amber-600 dark:text-amber-400' : 'font-bold text-slate-700 dark:text-slate-200'}`}>
                {s.toAirport || '···'}
              </span>
            </span>
          ))}
        </div>

        <div className="space-y-2">
          {stops.map((s, i) => {
            const legMins = s.departureTime && s.arrivalTime
              ? Math.round((new Date(s.arrivalTime).getTime() - new Date(s.departureTime).getTime()) / 60000)
              : null;
            const legDuration = legMins != null && legMins > 0
              ? legMins >= 60 ? `${Math.floor(legMins / 60)}h ${legMins % 60}m` : `${legMins}m`
              : null;
            const legNeg = legMins != null && legMins <= 0;
            return (
              <div key={i} className="rounded-xl border border-amber-200/70 dark:border-amber-900/40 bg-amber-50/40 dark:bg-amber-900/10 overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2 bg-white/60 dark:bg-slate-900/40 border-b border-amber-100 dark:border-amber-900/30">
                  <div className="w-6 h-6 rounded-md bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center shrink-0">
                    <Icon as={MapPin} size={12} className="text-amber-600 dark:text-amber-400" />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-amber-600 dark:text-amber-400 shrink-0">
                    {t('bookings.form.legNumber', { n: i + 1 })}
                  </span>
                  {legDuration && (
                    <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 ml-auto shrink-0">
                      {legDuration}
                    </span>
                  )}
                  {legNeg && (
                    <span className="text-[10px] font-semibold text-red-500 ml-auto shrink-0">
                      {t('bookings.form.arrivalBeforeDeparture')}
                    </span>
                  )}
                  {stops.length > 1 && (
                    <button type="button" onClick={() => removeStop(i)}
                      className={`w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 cursor-pointer shrink-0 ${legDuration || legNeg ? '' : 'ml-auto'}`}
                      aria-label={t('bookings.form.removeLeg')}>
                      <Icon as={Trash2} size={13} />
                    </button>
                  )}
                </div>

                <div className="p-2.5 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 mb-1">{t('bookings.form.from')}</label>
                      <input
                        className={'input uppercase font-mono font-semibold tracking-wider' + err(submitted && !s.fromAirport.trim())}
                        value={s.fromAirport}
                        onChange={e => updateStop(i, { fromAirport: e.target.value.toUpperCase() })}
                        placeholder="MAD"
                        maxLength={10}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 mb-1">{t('bookings.form.to')}</label>
                      <input
                        className={'input uppercase font-mono font-semibold tracking-wider' + err(submitted && !s.toAirport.trim())}
                        value={s.toAirport}
                        onChange={e => updateStop(i, { toAirport: e.target.value.toUpperCase() })}
                        placeholder="CDG"
                        maxLength={10}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 mb-1">{t('bookings.form.departure')}</label>
                      <DatePicker withTime value={s.departureTime} onChange={d => updateStop(i, { departureTime: d })} defaultMonth={tripStartDate} error={submitted && !s.departureTime} />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 mb-1">{t('bookings.form.arrival')}</label>
                      <DatePicker withTime value={s.arrivalTime} onChange={d => updateStop(i, { arrivalTime: d })} defaultMonth={s.departureTime || tripStartDate} error={submitted && !s.arrivalTime} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 mb-1">{t('bookings.form.flightNumber')}</label>
                      <input className="input" value={s.flightNumber} onChange={e => updateStop(i, { flightNumber: e.target.value })} placeholder="IB3456" maxLength={20} />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 mb-1">{t('bookings.form.airline')}</label>
                      <input className="input" value={s.airline} onChange={e => updateStop(i, { airline: e.target.value })} placeholder="Iberia" maxLength={100} />
                    </div>
                  </div>
                  <ReminderField value={s.reminderMinutes} onChange={m => updateStop(i, { reminderMinutes: m })} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Passengers ── */}
      {allParticipants.length > 0 && (
        <div>
          <label className={FIELD_LABEL}>{t('bookings.form.passengers')}</label>
          <div className="flex flex-wrap gap-2">
            {allParticipants.map(p => {
              const selected = selectedParticipantIds.includes(p.userId);
              const name = p.user.displayName || p.user.username;
              return (
                <button
                  key={p.userId}
                  type="button"
                  onClick={() => setSelectedParticipantIds(prev =>
                    selected ? prev.filter(id => id !== p.userId) : [...prev, p.userId]
                  )}
                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded-xl border text-xs font-medium transition-colors cursor-pointer ${
                    selected
                      ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300'
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:border-slate-300'
                  }`}
                >
                  <UserAvatar name={name} avatarUrl={p.user.avatarUrl} size={20} />
                  {name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <label className={FIELD_LABEL}>{t('bookings.form.notes')}</label>
        <textarea className="input" value={v.notes} onChange={e => setV(x => ({ ...x, notes: e.target.value }))} rows={2} />
      </div>

      <DocumentAttachments
        existing={existingDocs}
        onDeleteExisting={doc => removeExistingDoc(doc as FlightDocument)}
        docToDelete={docToDelete}
        onRequestDelete={doc => setDocToDelete(doc as FlightDocument | null)}
        staged={files.stagedFiles}
        onAddFiles={files.addFiles}
        onRemoveStaged={files.removeStaged}
        fileInputKey={files.fileInputKey}
        onExtract={files.runExtraction}
        extracting={files.extracting}
      />

      <div className="flex gap-2 pt-1">
        <button type="button" onClick={onCancel} className="flex-1 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-semibold text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">{t('common.cancel')}</button>
        <Button type="submit" variant="cta" loading={busy} className="flex-1 justify-center py-2 text-sm disabled:opacity-50 disabled:pointer-events-none">
          {stepIndex < extractedGroups.length - 1
            ? t('bookings.form.saveAndContinue')
            : initial ? t('bookings.form.saveChanges') : t('bookings.form.addFlight')}
        </Button>
      </div>
    </form>
  );
}
